import { useState, type ReactNode } from "react";
import AdminSidebar from "./AdminSidebar";
import { useTranslation } from "react-i18next";
import { useAdminLang, type AdminLang } from "../../../contexts/AdminLangContext";
import { useAdminAuth } from "../../../contexts/AdminAuthContext";
import { useAdminT } from "../../../hooks/useAdminT";

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}

function AdminLangSwitcher() {
  const { adminLang, setAdminLang } = useAdminLang();
  const { t } = useTranslation();
  const langs: Array<{ code: AdminLang; label: string }> = [
    { code: "zh", label: t("tabifotoAdmin.lang.zh") },
    { code: "en", label: t("tabifotoAdmin.lang.en") },
    { code: "ja", label: t("tabifotoAdmin.lang.ja") },
  ];
  return (
    <div className="flex items-center gap-1 bg-[#F7F6F3] p-0.5 rounded-lg">
      {langs.map((l) => (
        <button
          key={l.code}
          onClick={() => setAdminLang(l.code)}
          className={`px-2.5 py-1 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${
            adminLang === l.code
              ? "bg-white text-[#2C2C2C]"
              : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

function AdminLayoutInner({ children, title, subtitle, action }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  useAdminAuth();

  return (
    <div className="flex h-screen bg-[#F7F6F3] overflow-hidden">
      <AdminSidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-[#D9D9D9]/60 px-8 py-4 flex items-center justify-between shrink-0 gap-4">
          <div className="min-w-0">
            <h1 className="text-lg font-light text-[#2C2C2C] tracking-wide truncate">{title}</h1>
            {subtitle && (
              <p className="text-xs text-[#2C2C2C]/40 mt-0.5 font-light truncate">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <AdminLangSwitcher />
            {action && <div>{action}</div>}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-8">{children}</main>
      </div>
    </div>
  );
}

export default function AdminLayout(props: AdminLayoutProps) {
  return <AdminLayoutInner {...props} />;
}
